import React, { useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear, faPen, faTrashCan, faUsers } from "@fortawesome/free-solid-svg-icons";
import api from "../api/API";
import MembersManager from "../components/MembersManager";
import { useWorkspace } from "../context/WorkspaceContext";
import "./WorkspacesPage.css";

const canManageWorkspace = (workspace) => ["owner", "admin"].includes(String(workspace?.papel || "").toLowerCase());

const getDuplicateMap = (workspaces) => {
  const counts = new Map();

  workspaces.forEach((workspace) => {
    const key = String(workspace.nome || "").trim().toLowerCase();
    if (!key) return;
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  return counts;
};

export default function WorkspacesPage() {
  const {
    workspaces,
    selectedWorkspaceId,
    setSelectedWorkspaceId,
    selectedWorkspace,
    loading,
    error,
    refreshWorkspaces,
    createWorkspace,
  } = useWorkspace();
  const [editingWorkspace, setEditingWorkspace] = useState(null);
  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceDescription, setWorkspaceDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [archivingId, setArchivingId] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [membersWorkspaceId, setMembersWorkspaceId] = useState("");

  const duplicateMap = useMemo(() => getDuplicateMap(workspaces), [workspaces]);
  const duplicateCount = useMemo(
    () =>
      Array.from(duplicateMap.values()).reduce(
        (total, count) => total + (count > 1 ? count : 0),
        0
      ),
    [duplicateMap]
  );

  const openEditModal = (workspace) => {
    setEditingWorkspace(workspace);
    setWorkspaceName(workspace.nome || "");
    setWorkspaceDescription(workspace.descricao || "");
    setFeedback(null);
  };

  const openCreateModal = () => {
    setEditingWorkspace(null);
    setWorkspaceName("");
    setWorkspaceDescription("");
    setFeedback(null);
    setShowCreateModal(true);
  };

  const closeModal = () => {
    setEditingWorkspace(null);
    setShowCreateModal(false);
    setWorkspaceName("");
    setWorkspaceDescription("");
  };

  const handleSaveWorkspace = async () => {
    const normalizedName = workspaceName.trim();

    if (!normalizedName) {
      setFeedback({ variant: "warning", message: "Informe o nome do workspace." });
      return;
    }

    setSaving(true);
    setFeedback(null);

    try {
      if (editingWorkspace) {
        await api.put(`/workspaces/${editingWorkspace.id}`, {
          nome: normalizedName,
          descricao: workspaceDescription.trim(),
        });
        await refreshWorkspaces();
        setFeedback({ variant: "success", message: "Workspace atualizado com sucesso." });
      } else {
        await createWorkspace({
          nome: normalizedName,
          descricao: workspaceDescription.trim(),
        });
        setFeedback({ variant: "success", message: "Workspace criado com sucesso." });
      }

      closeModal();
    } catch (saveError) {
      setFeedback({
        variant: "danger",
        message:
          saveError?.response?.data?.message ||
          saveError?.message ||
          "Nao foi possivel salvar o workspace.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveWorkspace = async (workspace) => {
    const confirmed = window.confirm(`Arquivar o workspace "${workspace.nome}"?`);
    if (!confirmed) return;

    setArchivingId(workspace.id);
    setFeedback(null);

    try {
      await api.delete(`/workspaces/${workspace.id}`);
      await refreshWorkspaces();
      setFeedback({ variant: "success", message: "Workspace arquivado com sucesso." });
    } catch (archiveError) {
      setFeedback({
        variant: "danger",
        message:
          archiveError?.response?.data?.message ||
          archiveError?.message ||
          "Nao foi possivel arquivar o workspace.",
      });
    } finally {
      setArchivingId("");
    }
  };

  const modalOpen = Boolean(editingWorkspace) || showCreateModal;

  return (
    <Container fluid className="workspaces-page">
      <section className="workspaces-hero">
        <div>
          <p className="workspaces-kicker">Perfil</p>
          <h1>Workspaces</h1>
          <p className="workspaces-subtitle">
            Organize ambientes, nomes duplicados, membros e acesso sem sair da sua conta.
          </p>
        </div>

        <Button variant="dark" onClick={openCreateModal}>
          <FontAwesomeIcon icon={faGear} /> Novo workspace
        </Button>
      </section>

      {error && <Alert variant="warning">{error}</Alert>}
      {feedback && <Alert variant={feedback.variant}>{feedback.message}</Alert>}

      <Row className="g-3 workspaces-summary">
        <Col md={4}>
          <Card>
            <Card.Body>
              <span>Total</span>
              <strong>{workspaces.length}</strong>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <span>Ativo</span>
              <strong>{selectedWorkspace?.nome || "Nenhum"}</strong>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className={duplicateCount > 0 ? "has-duplicates" : ""}>
            <Card.Body>
              <span>Possiveis duplicados</span>
              <strong>{duplicateCount}</strong>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {loading ? (
        <div className="workspaces-loading">
          <Spinner size="sm" /> Carregando workspaces...
        </div>
      ) : (
        <div className="workspaces-list">
          {workspaces.map((workspace) => {
            const duplicateKey = String(workspace.nome || "").trim().toLowerCase();
            const isDuplicate = (duplicateMap.get(duplicateKey) || 0) > 1;
            const canManage = canManageWorkspace(workspace);
            const selected = workspace.id === selectedWorkspaceId;

            return (
              <Card key={workspace.id} className={`workspace-admin-card ${selected ? "selected" : ""} ${isDuplicate ? "duplicate" : ""}`}>
                <Card.Body>
                  <div className="workspace-admin-top">
                    <div>
                      <div className="workspace-title-row">
                        <h2>{workspace.nome}</h2>
                        {isDuplicate && <Badge bg="warning" text="dark">Duplicado</Badge>}
                        <Badge bg={workspace.papel === "owner" ? "dark" : "light"} text={workspace.papel === "owner" ? undefined : "dark"}>
                          {workspace.papel || "member"}
                        </Badge>
                      </div>
                      <p>{workspace.descricao || "Sem descricao cadastrada."}</p>
                    </div>

                    <div className="workspace-card-actions">
                      <Button
                        variant={selected ? "primary" : "outline-primary"}
                        size="sm"
                        onClick={() => setSelectedWorkspaceId(workspace.id)}
                      >
                        {selected ? "Selecionado" : "Selecionar"}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setMembersWorkspaceId((current) => (current === workspace.id ? "" : workspace.id))}
                      >
                        <FontAwesomeIcon icon={faUsers} /> Membros
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={!canManage}
                        onClick={() => openEditModal(workspace)}
                      >
                        <FontAwesomeIcon icon={faPen} /> Editar
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={!canManage || archivingId === workspace.id}
                        onClick={() => handleArchiveWorkspace(workspace)}
                      >
                        <FontAwesomeIcon icon={faTrashCan} /> {archivingId === workspace.id ? "Arquivando..." : "Arquivar"}
                      </Button>
                    </div>
                  </div>

                  {!canManage && (
                    <div className="workspace-note">Seu papel permite visualizar e selecionar, mas nao editar este workspace.</div>
                  )}

                  {membersWorkspaceId === workspace.id && (
                    <div className="workspace-members-panel">
                      <MembersManager workspaceId={workspace.id} />
                    </div>
                  )}
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}

      <Modal show={modalOpen} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingWorkspace ? "Editar workspace" : "Novo workspace"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome</Form.Label>
              <Form.Control
                type="text"
                value={workspaceName}
                onChange={(event) => setWorkspaceName(event.target.value)}
                placeholder="Ex.: GooMo"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Descricao</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={workspaceDescription}
                onChange={(event) => setWorkspaceDescription(event.target.value)}
                placeholder="Objetivo deste workspace"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveWorkspace} disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
